/**
 * Hub usage ping (Sep 2026): once per app per 30 min per tab, fire-and-forget.
 * Aggregated-only stats (see shared/src/hubUsage.ts); skipped while impersonating
 * and inside the split-view frame so nothing is double-counted.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { CALLABLES, type HubUsagePingInput, type HubUsageEngagementInput } from '@daaily/shared';
import { functions } from './firebase';
import { isStandalone } from '../applock/appLock';
import { isEmbedded } from '../components/SplitView';

const call = httpsCallable<HubUsagePingInput, { ok: true }>(functions, CALLABLES.HUB_USAGE_PING);
const callEngagement = httpsCallable<HubUsageEngagementInput, { ok: true }>(functions, CALLABLES.HUB_USAGE_ENGAGEMENT);
const WINDOW_MS = 30 * 60_000;

function device(): HubUsagePingInput['device'] {
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  if (!coarse) return 'desktop';
  return Math.min(window.screen.width, window.screen.height) < 600 ? 'phone' : 'tablet';
}

export function useUsagePing(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isEmbedded()) return;
    try { if (sessionStorage.getItem('daaily.impersonation')) return; } catch { /* ignore */ }
    const app = (pathname.split('/')[1] ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40);
    const key = `daaily.usage.${app || 'home'}`;
    try {
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < WINDOW_MS) return;
      sessionStorage.setItem(key, String(Date.now()));
    } catch { /* storage blocked: ping anyway */ }
    void call({ app, device: device(), mode: isStandalone() ? 'app' : 'browser' }).catch(() => undefined);
  }, [pathname]);
}

// ── Engagement (Sep 2026): active time per page, max scroll depth, sessions ──
// Aggregated only: the batch carries no identifier (see shared/src/hubUsage.ts).
const TICK_MS = 5_000;
const IDLE_MS = 2 * 60_000;       // no input for 2 min → time stops counting
const SESSION_GAP_MS = 30 * 60_000; // 30 min without activity → next activity starts a new session
const QUEUE_KEY = 'daaily.usage.queue';
const SESSION_KEY = 'daaily.usage.lastActive';
const FLUSH_MS = 60_000;

type Item = HubUsageEngagementInput['items'][number];
interface Queue { items: Item[]; sessions: number }

/** Route → page key: lowercase, ids and emails replaced by ':id', max 3 segments, '~' separated. */
export function pageKey(pathname: string): string {
  const segs = pathname.split('/').filter(Boolean).slice(0, 3).map((raw) => {
    let x = raw.toLowerCase();
    try { x = decodeURIComponent(raw).toLowerCase(); } catch { /* malformed escape: keep raw */ }
    if (/^\d{4}-\d{2}(-\d{2})?$/.test(x)) return ':date';
    if (/[@%]|^\d+$|^[0-9a-f-]{20,}$|^[a-z0-9_-]{15,}$/.test(x) || /\d/.test(x) && x.length >= 8) return ':id';
    return x.replace(/[^a-z0-9-]/g, '').slice(0, 24) || ':id';
  });
  return (segs.join('~') || 'home').slice(0, 80);
}

function readQueue(): Queue {
  try { const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || 'null') as Queue | null; if (q && Array.isArray(q.items)) return q; } catch { /* ignore */ }
  return { items: [], sessions: 0 };
}
function writeQueue(q: Queue) { try { localStorage.setItem(QUEUE_KEY, JSON.stringify({ items: q.items.slice(-80), sessions: q.sessions })); } catch { /* ignore */ } }
let flushing = false;
async function flush() {
  if (flushing) return;
  const q = readQueue();
  if (q.items.length === 0 && q.sessions === 0) return;
  flushing = true;
  const batch = { items: q.items.slice(0, 40), sessions: Math.min(q.sessions, 10) };
  try {
    await callEngagement(batch);
    const now = readQueue(); // items queued meanwhile stay
    writeQueue({ items: now.items.slice(batch.items.length), sessions: Math.max(0, now.sessions - batch.sessions) });
  } catch { /* keep for the next try */ } finally { flushing = false; }
}

interface Visit { page: string; activeMs: number; maxScroll: number; scrollable: boolean }
function scrollRatio(): { pct: number; scrollable: boolean } {
  const doc = document.documentElement;
  const h = doc.scrollHeight;
  const view = window.innerHeight;
  const scrollable = h > view + 40;
  const pct = scrollable ? Math.min(100, Math.round(((window.scrollY + view) / h) * 100)) : 100;
  return { pct, scrollable };
}

export function useEngagementTracking(): void {
  const { pathname } = useLocation();
  // One tracker per tab; route changes swap the current visit.
  useEffect(() => {
    if (isEmbedded()) return;
    try { if (sessionStorage.getItem('daaily.impersonation')) return; } catch { /* ignore */ }
    let lastInput = Date.now();
    const onInput = () => { lastInput = Date.now(); };
    const onScroll = () => {
      onInput();
      const v = current.visit;
      if (!v) return;
      const r = scrollRatio();
      if (r.scrollable) { v.scrollable = true; v.maxScroll = Math.max(v.maxScroll, r.pct); }
    };
    const tick = () => {
      const v = current.visit;
      if (!v || document.visibilityState !== 'visible' || Date.now() - lastInput > IDLE_MS) return;
      v.activeMs += TICK_MS;
      // Session boundary: first active tick after a 30-min gap (across tabs, via localStorage).
      try {
        const last = Number(localStorage.getItem(SESSION_KEY) || 0);
        if (Date.now() - last > SESSION_GAP_MS) { const q = readQueue(); q.sessions += 1; writeQueue(q); }
        localStorage.setItem(SESSION_KEY, String(Date.now()));
      } catch { /* ignore */ }
    };
    const onHidden = () => { if (document.visibilityState === 'hidden') { finishVisit(); void flush(); } else { startVisit(current.page); } };
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
    events.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onHidden);
    const t1 = window.setInterval(tick, TICK_MS);
    const t2 = window.setInterval(() => void flush(), FLUSH_MS);
    void flush(); // leftovers from an earlier visit
    return () => {
      finishVisit(); void flush();
      events.forEach((e) => window.removeEventListener(e, onInput));
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onHidden);
      window.clearInterval(t1); window.clearInterval(t2);
    };
  }, []);
  useEffect(() => {
    if (isEmbedded()) return;
    finishVisit();
    startVisit(pageKey(pathname));
    // Initial viewport share once the page has rendered.
    const t = window.setTimeout(() => {
      const v = current.visit; if (!v) return;
      const r = scrollRatio(); v.scrollable = r.scrollable; v.maxScroll = Math.max(v.maxScroll, r.pct);
    }, 1500);
    return () => window.clearTimeout(t);
  }, [pathname]);
}

const current: { page: string; visit: Visit | null } = { page: 'home', visit: null };
function startVisit(page: string) { current.page = page; current.visit = { page, activeMs: 0, maxScroll: 0, scrollable: false }; }
function finishVisit() {
  const v = current.visit;
  current.visit = null;
  if (!v || v.activeMs < 2_000) return;
  const q = readQueue();
  q.items.push({ page: v.page, seconds: Math.min(4 * 3600, Math.round(v.activeMs / 1000)), scroll: v.scrollable ? v.maxScroll : null });
  writeQueue(q);
}

/** Mount once inside the router. */
export function UsagePing(): null {
  useUsagePing();
  useEngagementTracking();
  return null;
}
